import {
  collection,
  query,
  where,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'

export type ExportJobStatus = 'QUEUED' | 'RUNNING' | 'READY' | 'FAILED' | 'EXPIRED'
export type ExportJobKind = 'PERSONAL' | 'WORKSPACE'

export interface TimestampLike {
  toMillis?: () => number
  toDate?: () => Date
}

export interface ExportJobClient {
  id: string
  kind: ExportJobKind
  uid: string
  requestedBy: string
  workspaceId?: string
  status: ExportJobStatus
  createdAt?: TimestampLike | string | number | null
  completedAt?: TimestampLike | string | number | null
  expiresAt?: TimestampLike | string | number | null
  storagePath?: string
  fileCount?: number
  byteSize?: number
  errorCode?: string
}

export interface RequestPersonalExportResult {
  ok: true
  jobId: string
  status: 'QUEUED'
  createdAt: string
}

export interface RequestWorkspaceExportResult {
  ok: true
  jobId: string
  status: 'QUEUED'
  createdAt: string
}

export interface GetExportDownloadUrlResult {
  ok: true
  downloadUrl: string
  expiresAt: string
}

export const requestPersonalExport = async (): Promise<RequestPersonalExportResult> => {
  return callable<Record<string, never>, RequestPersonalExportResult>('requestPersonalExport', {})
}

export const requestWorkspaceExport = async (
  workspaceId: string,
): Promise<RequestWorkspaceExportResult> => {
  return callable<{ workspaceId: string }, RequestWorkspaceExportResult>(
    'requestWorkspaceExport',
    { workspaceId },
  )
}

export const getExportDownloadUrl = async (
  jobId: string,
): Promise<GetExportDownloadUrlResult> => {
  return callable<{ jobId: string }, GetExportDownloadUrlResult>(
    'getExportDownloadUrl',
    { jobId },
  )
}

export const ownerRequestPersonalExport = async (
  uid: string,
  accessReason?: unknown,
): Promise<{ ok: true; jobId: string; status: 'QUEUED'; createdAt: string }> => {
  return callable<{ uid: string; accessReason?: unknown }, { ok: true; jobId: string; status: 'QUEUED'; createdAt: string }>(
    'ownerRequestPersonalExport',
    { uid, accessReason },
  )
}

const getJobTimestampMs = (val: unknown): number => {
  if (!val) return 0
  if (typeof val === 'object' && val !== null) {
    if ('toMillis' in val && typeof (val as { toMillis: unknown }).toMillis === 'function') {
      return (val as { toMillis: () => number }).toMillis()
    }
    if ('toDate' in val && typeof (val as { toDate: unknown }).toDate === 'function') {
      return (val as { toDate: () => Date }).toDate().getTime()
    }
  }
  if (typeof val === 'number') return val
  if (typeof val === 'string') {
    const t = Date.parse(val)
    return isNaN(t) ? 0 : t
  }
  return 0
}

export const subscribeToMyExportJobs = (
  uid: string,
  callback: (jobs: ExportJobClient[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe => {
  if (!uid) {
    callback([])
    return () => {}
  }
  const q = query(
    collection(firestoreDb, 'export_jobs'),
    where('uid', '==', uid),
    where('kind', '==', 'PERSONAL'),
  )
  return onSnapshot(
    q,
    (snap) => {
      const jobs: ExportJobClient[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<ExportJobClient, 'id'>),
      }))
      jobs.sort((a, b) => getJobTimestampMs(b.createdAt) - getJobTimestampMs(a.createdAt))
      callback(jobs)
    },
    (err) => {
      console.warn('[subscribeToMyExportJobs] snapshot error:', err)
      callback([])
      if (onError) onError(err as Error)
    },
  )
}

export const subscribeToWorkspaceExportJobs = (
  workspaceId: string,
  callback: (jobs: ExportJobClient[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe => {
  if (!workspaceId) {
    callback([])
    return () => {}
  }
  const q = query(
    collection(firestoreDb, 'export_jobs'),
    where('workspaceId', '==', workspaceId),
  )
  return onSnapshot(
    q,
    (snap) => {
      const jobs: ExportJobClient[] = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<ExportJobClient, 'id'>),
      }))
      jobs.sort((a, b) => getJobTimestampMs(b.createdAt) - getJobTimestampMs(a.createdAt))
      callback(jobs)
    },
    (err) => {
      console.warn('[subscribeToWorkspaceExportJobs] snapshot error:', err)
      callback([])
      if (onError) onError(err as Error)
    },
  )
}
